import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { equipmentItems } from '../data/equipmentData';
import { API_BASE_URL } from '../config';
import './Home.css';

const heroImages = [
  '/images/hero-bg-2.jpg',
  '/images/hero-bg-3.jpg',
];

const Home = () => {
  const [currentHeroImage, setCurrentHeroImage] = useState(0);
  const [currentArrivalIndex, setCurrentArrivalIndex] = useState(0);
  const [newArrivals, setNewArrivals] = useState([]);

  useEffect(() => {
    const rotationTimer = window.setInterval(() => {
      setCurrentHeroImage((prev) => (prev === heroImages.length - 1 ? 0 : prev + 1));
    }, 6000);

    return () => window.clearInterval(rotationTimer);
  }, []);

  useEffect(() => {
    fetch(`${API_BASE_URL}/new-arrivals`)
      .then(r => r.json())
      .then(d => setNewArrivals(d.arrivals || []))
      .catch(() => { });
  }, []);

  useEffect(() => {
    if (newArrivals.length <= 1) return;
    const timer = window.setInterval(() => {
      setCurrentArrivalIndex(prev => (prev === newArrivals.length - 1 ? 0 : prev + 1));
    }, 3500);
    return () => window.clearInterval(timer);
  }, [newArrivals.length]);

  const navigateHero = (direction) => {
    setCurrentHeroImage((prev) => {
      if (direction === 'left') {
        return prev === 0 ? heroImages.length - 1 : prev - 1;
      }

      return prev === heroImages.length - 1 ? 0 : prev + 1;
    });
  };

  const showNextArrival = () => {
    setCurrentArrivalIndex((prev) => (prev === newArrivals.length - 1 ? 0 : prev + 1));
  };

  const showPreviousArrival = () => {
    setCurrentArrivalIndex((prev) => (prev === 0 ? newArrivals.length - 1 : prev - 1));
  };

  const currentArrival = newArrivals[currentArrivalIndex];

  return (
    <div className="home">
      <section className="hero hero--showcase">
        <div className="hero-media-frame">
          <img
            src={heroImages[currentHeroImage]}
            alt="Takumi Tension badminton hero"
            className="hero-media-image"
          />
        </div>
        <div className="hero-overlay" />

        <button
          className="hero-nav-btn hero-nav-left"
          onClick={() => navigateHero('left')}
          aria-label="Previous image"
        >
          ❮
        </button>

        <div className="container hero-shell">
          <div className="hero-content">
            <h1 className="hero-title">Welcome to Takumi Tension<br />(匠のテンション)</h1>
            <div className="hero-buttons">
              <Link to="/services" className="cta-button secondary">
                Explore Services
              </Link>
              <Link to="/contact" className="cta-button secondary">
                Contact Us
              </Link>
            </div>
            <p className="hero-slogan">
              Expert stringing with precision and care for optimal performance
            </p>
          </div>
        </div>

        <button
          className="hero-nav-btn hero-nav-right"
          onClick={() => navigateHero('right')}
          aria-label="Next image"
        >
          ❯
        </button>

        <div className="hero-indicators">
          {heroImages.map((_, index) => (
            <button
              key={index}
              className={`hero-indicator ${index === currentHeroImage ? 'active' : ''}`}
              onClick={() => setCurrentHeroImage(index)}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {newArrivals.length > 0 && currentArrival && (
        <section className="arrivals-section">
          <div className="container">
            <div className="section-heading arrivals-heading">
              <span className="section-kicker">Fresh stock</span>
              <h2>New Arrivals</h2>
            </div>

            <div className="arrival-carousel">
              <button
                type="button"
                className="arrival-nav-btn"
                onClick={showPreviousArrival}
                aria-label="Previous new arrival"
                style={{ visibility: newArrivals.length > 1 ? 'visible' : 'hidden' }}
              >
                ❮
              </button>

              <article className="arrival-card">
                <div className="arrival-image-wrap">
                  <img
                    src={currentArrival.image}
                    alt={currentArrival.name}
                    className="arrival-image"
                  />
                </div>
                <div className="arrival-copy">
                  <span className="curated-label">{currentArrival.label}</span>
                  <h3>{currentArrival.name}</h3>
                  <p>{currentArrival.description}</p>
                </div>
              </article>

              <button
                type="button"
                className="arrival-nav-btn"
                onClick={showNextArrival}
                aria-label="Next new arrival"
                style={{ visibility: newArrivals.length > 1 ? 'visible' : 'hidden' }}
              >
                ❯
              </button>
            </div>
          </div>
        </section>
      )}

      <section className="certs-section">
        <div className="container">
          <div className="section-heading certs-heading">
            <span className="section-kicker">Why Choose Us</span>
            <h2>Trusted Skills, Proven Care</h2>
            <p className="certs-intro">
              Certified training, precision workmanship, and professional standards — giving every player confidence in our service.
            </p>
          </div>

          <div className="cert-single-wrap">
            <img
              src="/images/string_cert.jpeg"
              alt="Stringing Course Certificate"
              className="cert-single-img"
            />
            <p className="cert-single-caption">Stringing Course Certificate</p>
          </div>
        </div>
      </section>

      <section className="equip-section">
        <div className="container">
          <div className="section-heading equip-heading">
            <span className="section-kicker">The Craft Behind Every String</span>
            <h2>Equipment We Use</h2>
            <p className="equip-intro">
              Great stringing starts with the right tools. We invest in professional-grade equipment
              so every racquet leaves our hands with consistent tension, clean finishes, and the
              performance you expect on court.
            </p>
          </div>

          <div className="equip-grid">
            {equipmentItems.map((item) => (
              <div key={item.id} className="equip-item">
                <div className="equip-photo">
                  <img src={item.image} alt={item.name} className="equip-img" />
                </div>
                <h4 className="equip-name">{item.name}</h4>
                <p className="equip-tagline">{item.tagline}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
